import moment from 'moment';
import React from 'react';
import constants from '../utilities/constants';
import {
  Dashboard,
  Storefront,
  PersonSearch,
  CreditScore,
  Build,
  Engineering,
  PriorityHigh,
  Inventory2,
  Handyman,
  BusinessCenter,
  Timeline,
  LocalShipping,
  AccountBalance,
  Assignment,
  ViewWeek,
  Bolt,
  DirectionsCar,
  BuildCircle,
  Person,
  Logout,
  AttachMoney,
  CreditCard,
  Analytics,
  Store,
  AssignmentInd,
  AccountBalanceWallet,
  Brush,
  AdminPanelSettings,
  ManageAccounts,
  AssignmentTurnedIn,
  Insights,
  Folder,
  ReceiptLong,
  TrendingDown,
  Redeem,
  RssFeed,
  Map as MapIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { PHONE_ACCESS_KEYS } from '../pages/PhonePage/phone';
import { ListItemIcon, ListItemText, Box, Typography, MenuItem, Divider, Badge } from '@mui/material';

const iconMap = {
  dashboard: Dashboard,
  storefront: Storefront,
  person_search: PersonSearch,
  credit_score: CreditScore,
  build: Build,
  engineering: Engineering,
  priority_high: PriorityHigh,
  inventory_2: Inventory2,
  handyman: Handyman,
  business_center: BusinessCenter,
  timeline: Timeline,
  local_shipping: LocalShipping,
  account_balance: AccountBalance,
  assignment: Assignment,
  view_week: ViewWeek,
  bolt: Bolt,
  directions_car: DirectionsCar,
  build_circle: BuildCircle,
  person: Person,
  logout: Logout,
  attach_money: AttachMoney,
  credit_card: CreditCard,
  analytics: Analytics,
  store: Store,
  assignment_ind: AssignmentInd,
  account_balance_wallet: AccountBalanceWallet,
  brush: Brush,
  admin_panel_settings: AdminPanelSettings,
  manage_accounts: ManageAccounts,
  assignment_turned_in: AssignmentTurnedIn,
  insights: Insights,
  folder: Folder,
  receipt_long: ReceiptLong,
  trending_down: TrendingDown,
  redeem: Redeem,
  rss_feed: RssFeed,
  map: MapIcon,
  phone: PhoneIcon,
};

// Menu sections with their respective items
export const menuSections = [
  {
    title: 'Sales',
    icon: 'storefront',
    items: [
      {
        label: 'Deal Tracker',
        path: '/deal-tracker',
        permission: 'sales-dashboard',
        icon: 'view_week',
      },
      {
        label: 'Deal Dashboard',
        path: `/deal-dashboard/${moment().startOf('month').format("YYYY-MM-DD")}/${moment().endOf('month').format("YYYY-MM-DD")}`,
        permission: 'deal-dashboard',
        icon: 'attach_money',
      },
      {
        label: 'Consignment Dashboard',
        path: '/consignment-dashboard',
        permission: 'deal-dashboard',
        icon: 'assignment_ind',
      },
      {
        label: 'Process Pipeline',
        path: '/pipeline',
        permission: 'pipeline',
        icon: 'timeline',
      },
      {
        label: 'Leads',
        path: '/leads',
        permission: 'leads',
        icon: 'person_search',
        badge: 'newLeads',
      },
      {
        label: 'Phone',
        path: '/phone',
        anyOf: PHONE_ACCESS_KEYS,
        icon: 'phone',
        badge: 'phoneUnread',
      },
      {
        label: 'Lead Sources',
        path: '/lead-sources',
        permission: 'leads',
        icon: 'analytics',
      },
      {
        label: 'Rep Lead Performance',
        path: '/rep-performance',
        permission: 'deal-dashboard',
        icon: 'insights',
        badge: 'leadFollowups',
      },
      {
        label: 'Credit Apps',
        path: '/credit-apps',
        permission: 'credit-apps',
        icon: 'credit_score',
      },
      {
        label: 'Detail Dashboard',
        path: '/detail-dashboard',
        permission: null,
        icon: 'directions_car',
      },

    ],
  },
  {
    title: 'Service',
    icon: 'build',
    items: [
      {
        label: 'Mechanic Queue',
        path: '/mechanic-queue',
        permission: 'service-pipeline',
        icon: 'view_week',
      },
      {
        label: 'Parts Pipeline',
        path: '/parts-pipeline',
        permission: 'parts-pipeline',
        icon: 'inventory_2',
      },
      {
        label: 'Parts Inventory',
        path: '/parts-inventory',
        permission: 'parts-inventory',
        icon: 'inventory_2',
      },
      {
        label: 'Service Pipeline',
        path: '/service-pipeline',
        permission: 'service-pipeline',
        icon: 'engineering',
        badge: 'newServiceOrders',
      },
      {
        label: 'Mechanic Summary',
        path: `/mechanic-summary/${constants.getDefaultPayPeriod().startParam}`,
        permission: 'mechanic-summary',
        icon: 'handyman',
      },
      // Off the menu, still routed and reachable by URL: Paint Jobs Dashboard
      // (/paint-jobs-dashboard), Service Aging (/service-aging), Mechanic Clock
      // (/mechanic-clock), Intake Queue (/intake-queue), Service Priorities
      // (/service-priorities) and Mechanic Board (/mechanic-board). Detail
      // Dashboard moved to the Sales section.
    ],
  },
  {
    title: 'Backoffice',
    icon: 'business_center',
    items: [
      {
        label: 'Performance Dashboard',
        path: `/performance-dashboard/${moment().startOf('month').format("YYYY-MM")}/${moment().endOf('month').format("YYYY-MM")}`,
        permission: 'deal-dashboard',
        icon: 'analytics',
      },
      {
        label: 'Shipping Dashboard',
        path: '/shipping-dashboard',
        // The route gates on 'shipping-dashboard'. This said 'deal-dashboard',
        // so the item showed for people the page then bounced home.
        permission: 'shipping-dashboard',
        icon: 'local_shipping',
      },
      {
        label: 'Load Planner',
        path: '/load-planner',
        // Its own key: the map carries no money, the dashboard does.
        permission: 'load-planner',
        icon: 'map',
      },
      {
        label: 'Accounting Dashboard',
        path: '/accounting',
        permission: 'accounting',
        icon: 'account_balance',
      },
      {
        label: 'Title Processing',
        path: '/title-processing',
        permission: 'title-processing',
        icon: 'assignment',
      },
      {
        label: 'Vendor Dashboard',
        path: '/vendor-dashboard',
        permission: 'accounting',
        icon: 'store',
      },
      {
        label: 'Car Performance',
        path: '/car-performance',
        permission: 'deal-dashboard',
        icon: 'directions_car',
      },
      {
        label: 'Inventory Snapshot',
        path: '/inventory-snapshot',
        permission: 'deal-dashboard',
        icon: 'inventory_2',
      },
      {
        label: 'Contractor Payment',
        path: '/contractor-payment',
        permission: 'accounting',
        icon: 'account_balance_wallet',
      },
      {
        label: 'Bills',
        path: '/bills',
        permission: 'accounting',
        icon: 'receipt_long',
      },
      {
        label: 'Document Library',
        path: '/files',
        permission: 'documents',
        icon: 'folder',
      },
    ],
  },
  {
    title: 'Reports',
    icon: 'insights',
    items: [
      {
        label: 'Service Performance',
        path: `/reports/service-performance/${moment().startOf('month').format("YYYY-MM-DD")}/${moment().endOf('month').format("YYYY-MM-DD")}`,
        permission: 'accounting',
        icon: 'insights',
      },
      {
        label: 'Sales Performance',
        path: `/reports/sales-performance/${moment().subtract(2, 'months').startOf('month').format("YYYY-MM-DD")}/${moment().endOf('month').format("YYYY-MM-DD")}`,
        permission: 'accounting',
        icon: 'insights',
      },
      {
        label: 'Inventory Performance',
        path: `/reports/inventory-performance/${moment().subtract(2, 'months').startOf('month').format("YYYY-MM-DD")}/${moment().endOf('month').format("YYYY-MM-DD")}`,
        permission: 'accounting',
        icon: 'insights',
      },
      {
        label: 'Car Demand & Views',
        path: '/reports/car-demand',
        permission: 'accounting',
        icon: 'insights',
      },
      {
        label: 'Funding Performance',
        path: '/reports/funding-performance',
        permission: 'accounting',
        icon: 'insights',
      },
      {
        label: 'Inventory Distribution',
        path: '/reports/inventory-distribution',
        permission: 'accounting',
        icon: 'donut_large',
      },
      {
        label: 'Sales Tax',
        path: `/sales-tax/${moment().startOf('month').format("YYYY-MM-DD")}/${moment().endOf('month').format("YYYY-MM-DD")}`,
        permission: 'accounting',
        icon: 'receipt',
      },
    ],
  },
  {
    title: 'Quick Actions',
    icon: 'bolt',
    items: [
      {
        label: 'Add Car',
        action: 'addCar',
        permission: 'add-car',
        icon: 'directions_car',
        color: 'primary',
      },
      {
        label: 'Add Service Order',
        action: 'addServiceOrder',
        permission: 'service-pipeline',
        icon: 'build_circle',
        color: 'secondary',
      },
      {
        label: 'Add Customer',
        action: 'addCustomer',
        permission: 'customer',
        icon: 'person',
        color: 'success',
      },
      {
        label: 'Add Lead',
        action: 'addLead',
        permission: 'leads',
        icon: 'person_search',
        color: 'info',
      },
      {
        label: 'Bulk Add Purchases',
        action: 'bulkPurchase',
        permission: 'accounting',
        icon: 'attach_money',
      },
      {
        label: 'Add Lender Incentives',
        action: 'bulkIncentive',
        permission: 'accounting',
        icon: 'redeem',
      },
      {
        label: 'Create Shipping Label',
        action: 'createShippingLabel',
        permission: 'shipping-label',
        icon: 'local_shipping',
      },
    ],
  },
  {
    title: 'Admin',
    icon: 'admin_panel_settings',
    items: [
      {
        label: 'User Management',
        path: '/user-management',
        permission: 'user-management',
        icon: 'manage_accounts',
      },
      {
        label: 'Lead Assignment',
        path: '/lead-assignment',
        permission: 'user-management',
        icon: 'assignment_turned_in',
      },
      {
        label: 'Lender Management',
        path: '/lenders',
        permission: 'bank-management',
        icon: 'account_balance',
      },
      {
        label: 'Price Strategy',
        path: '/price-strategy',
        permission: 'price-strategy',
        icon: 'trending_down',
      },
      {
        label: 'Ron Check-In',
        path: '/ron-checkin',
        permission: 'ron-checkin',
        icon: 'attach_money',
      },
      {
        label: 'Signature Templates',
        path: '/signature-templates',
        permission: 'esign-templates',
        icon: 'brush',
      },
      {
        label: 'Feed Health',
        path: '/feed-health',
        permission: 'feed-health',
        icon: 'rss_feed',
      },
      {
        label: 'Inventory Email',
        path: '/inventory-email',
        permission: 'inventory-email',
        icon: 'mark_email_read',
      },
    ],
  },
];

// User section (always at the bottom)
export const userSection = {
  title: 'User',
  icon: 'person',
  items: [
    {
      label: 'Sign Out',
      action: 'signOut',
      permission: null,
      icon: 'logout',
      color: 'error',
    },
  ],
};

function menuItemAllowed(item, StateManager) {
  if (StateManager.isAdmin()) return true;
  if (Array.isArray(item.anyOf) && item.anyOf.length) {
    return item.anyOf.some(key => StateManager.isUserAllowed(key));
  }
  if (item.permission) return StateManager.isUserAllowed(item.permission);
  return true;
}

export function renderMenuSections(StateManager, navigate, handleAction, classes, badges = {}) {
  return <>
    {menuSections.map((section) => (
      <Box key={section.title} className={classes.menuSection}>
        <Typography className={classes.menuSectionTitle}>
          {iconMap[section.icon] && React.createElement(iconMap[section.icon], { fontSize: 'small' })}
          {section.title}
        </Typography>
        {section.items.map((item) => {
          if (!menuItemAllowed(item, StateManager)) return null;
          const IconComponent = iconMap[item.icon];
          const badgeCount = item.badge ? badges[item.badge] : 0;
          return (
            <MenuItem
              key={item.label}
              className={classes.menuItem}
              onClick={() => item.path ? navigate(item.path) : handleAction(item.action)}
              sx={[
                item.color ? { color: `${item.color}.main` } : {},
                { pl: 4 }
              ]}
            >
              {IconComponent && (
                <ListItemIcon className={classes.menuItemIcon}>
                  {React.createElement(IconComponent)}
                </ListItemIcon>
              )}
              <ListItemText
                primary={item.label}
                className={classes.menuItemText}
              />
              {badgeCount > 0 && (
                <Badge
                  badgeContent={badgeCount}
                  color="warning"
                  sx={{ mr: 1.5, '& .MuiBadge-badge': { fontWeight: 700, position: 'static', transform: 'none' } }}
                />
              )}
            </MenuItem>
          );
        })}
      </Box>
    ))}
    <Divider className={classes.divider} />
    <Box className={classes.menuSection}>
      <Typography className={classes.menuSectionTitle}>
        {iconMap[userSection.icon] && React.createElement(iconMap[userSection.icon], { fontSize: 'small' })}
        {userSection.title}
      </Typography>
      {userSection.items.map((item) => {
        if (item.permission && !StateManager.isUserAllowed(item.permission)) {
          return null;
        }
        const IconComponent = iconMap[item.icon];
        return (
          <MenuItem
            key={item.label}
            className={classes.menuItem}
            onClick={() => item.path ? navigate(item.path) : handleAction(item.action)}
            sx={[
              item.color ? { color: `${item.color}.main` } : {},
              { pl: 4 }
            ]}
          >
            {IconComponent && (
              <ListItemIcon className={classes.menuItemIcon}>
                {React.createElement(IconComponent)}
              </ListItemIcon>
            )}
            <ListItemText 
              primary={item.label}
              className={classes.menuItemText}
            />
          </MenuItem>
        );
      })}
    </Box>
  </>;
} 